import { useCallback, useEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router';

import type { Notification } from '@enterprise/api';
import { isDefined, isNotDefined } from '@enterprise/core';

import { useDeleteAllNotifications } from '@feat/notifications/hooks/mutations/delete-all-notifications.hook';
import { useDeleteNotification } from '@feat/notifications/hooks/mutations/delete-notification.hook';
import { useReadAllNotifications } from '@feat/notifications/hooks/mutations/read-all-notifications.hook';
import { useReadNotification } from '@feat/notifications/hooks/mutations/read-notification.hook';
import { useNativeNotification } from '@feat/notifications/hooks/native-notification.hook';
import { useNotificationSse } from '@feat/notifications/hooks/notification-sse.hook';
import { useGetNotifications } from '@feat/notifications/hooks/queries/get-notifications.hook';
import { resolveEntityRoute } from '@feat/notifications/utilities/entity-route';

import type { NotificationBellConfiguration } from './types/notification-bell-configuration.type';

export const useNotificationBellConfiguration = (): NotificationBellConfiguration => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [ring, setRing] = useState(false);
  const [bump, setBump] = useState(false);
  const [freshId, setFreshId] = useState<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  // Data
  const { data: notifications = [] } = useGetNotifications();

  // Mutations
  const readNotification = useReadNotification();
  const readAllNotifications = useReadAllNotifications();
  const deleteNotification = useDeleteNotification();
  const deleteAllNotifications = useDeleteAllNotifications();

  // Native notifications
  const {
    permission: nativePermission,
    raise: raiseNative,
    requestPermission: onRequestNativePermission,
  } = useNativeNotification();

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onToggle = useCallback(() => {
    setIsOpen((prev) => {
      return !prev;
    });
  }, []);

  const triggerRingBump = useCallback((id: number) => {
    setFreshId(id);
    setRing(true);
    setBump(true);

    const ringTimer = setTimeout(() => {
      setRing(false);
    }, 600);

    const bumpTimer = setTimeout(() => {
      setBump(false);
    }, 300);

    return () => {
      clearTimeout(ringTimer);
      clearTimeout(bumpTimer);
    };
  }, []);

  // SSE — mounts once, keeps cache live
  useNotificationSse({ onNew: triggerRingBump, raiseNative });

  // Outside-click and Escape close
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isOpen, onClose]);

  // Derived
  const unreadCount = notifications.filter((notification) => {
    return isNotDefined(notification.readDate);
  }).length;

  const onOpen = useCallback(
    (notification: Notification) => {
      readNotification.mutate({ id: notification.id });

      const route = resolveEntityRoute(notification.entityType, notification.entityId);

      if (isDefined(route)) {
        void navigate(route);
        onClose();
      }
    },
    [readNotification, navigate, onClose]
  );

  const onDelete = useCallback(
    (id: number) => {
      deleteNotification.mutate({ id });
    },
    [deleteNotification]
  );

  const onReadAll = useCallback(() => {
    readAllNotifications.mutate();
  }, [readAllNotifications]);

  const onDeleteAll = useCallback(() => {
    deleteAllNotifications.mutate();
  }, [deleteAllNotifications]);

  return {
    bump,
    containerRef,
    freshId,
    isOpen,
    nativePermission,
    notifications,
    onClose,
    onDelete,
    onDeleteAll,
    onOpen,
    onReadAll,
    onRequestNativePermission,
    onToggle,
    ring,
    unreadCount,
  };
};
